import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../utils/supabaseClient';
import { User as SupabaseUser } from '@supabase/supabase-js';
interface User {
  id: string;
  name: string;
  email: string;
  university?: string;
  courseOfStudy?: string;
  graduationYear?: string;
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{
    error?: string;
  }>;
  signup: (user: Omit<User, 'id'>, password: string) => Promise<{
    error?: string;
  }>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  // Initialize auth state from Supabase session
  useEffect(() => {
    const initializeAuth = async () => {
      const {
        data
      } = await supabase.auth.getSession();
      if (data.session) {
        const supaUser = data.session.user;
        // Get user profile data from users table if it exists
        const {
          data: profileData
        } = await supabase.from('profiles').select('*').eq('id', supaUser.id).single();
        const userData: User = {
          id: supaUser.id,
          email: supaUser.email || '',
          name: profileData?.name || supaUser.email?.split('@')[0] || 'User',
          university: profileData?.university || '',
          courseOfStudy: profileData?.courseOfStudy || '',
          graduationYear: profileData?.graduationYear || ''
        };
        setUser(userData);
        setIsAuthenticated(true);
      }
      setLoading(false);
      // Set up auth state change listener
      const {
        data: authListener
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (session) {
          const supaUser = session.user;
          // Get user profile data
          const {
            data: profileData
          } = await supabase.from('profiles').select('*').eq('id', supaUser.id).single();
          const userData: User = {
            id: supaUser.id,
            email: supaUser.email || '',
            name: profileData?.name || supaUser.email?.split('@')[0] || 'User',
            university: profileData?.university || '',
            courseOfStudy: profileData?.courseOfStudy || '',
            graduationYear: profileData?.graduationYear || ''
          };
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      });
      return () => {
        authListener.subscription.unsubscribe();
      };
    };
    initializeAuth();
  }, []);
  const login = async (email: string, password: string) => {
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) {
        return {
          error: error.message
        };
      }
      return {};
    } catch (error) {
      console.error('Login error:', error);
      return {
        error: 'An unexpected error occurred during login'
      };
    }
  };
  const signup = async (userData: Omit<User, 'id'>, password: string) => {
    try {
      // Register the user with Supabase Auth
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email: userData.email,
        password: password
      });
      if (error) {
        return {
          error: error.message
        };
      }
      if (data.user) {
        // Insert the user's profile data into the profiles table
        const {
          error: profileError
        } = await supabase.from('profiles').insert([{
          id: data.user.id,
          name: userData.name,
          email: userData.email,
          university: userData.university,
          courseOfStudy: userData.courseOfStudy,
          graduationYear: userData.graduationYear
        }]);
        if (profileError) {
          console.error('Error creating profile:', profileError);
          return {
            error: 'Profile creation failed'
          };
        }
      }
      return {};
    } catch (error) {
      console.error('Signup error:', error);
      return {
        error: 'An unexpected error occurred during signup'
      };
    }
  };
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAuthenticated(false);
  };
  const updateProfile = async (data: Partial<User>) => {
    if (!user) return;
    try {
      // Update the profile in the database
      const {
        error
      } = await supabase.from('profiles').update({
        name: data.name,
        university: data.university,
        courseOfStudy: data.courseOfStudy,
        graduationYear: data.graduationYear
      }).eq('id', user.id);
      if (error) {
        console.error('Error updating profile:', error);
        return;
      }
      // Update local state
      setUser({
        ...user,
        ...data
      });
    } catch (error) {
      console.error('Profile update error:', error);
    }
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    login,
    signup,
    logout,
    updateProfile
  }} data-id="element-312">
      {!loading && children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};